import { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import {
  useValidEmail,
  useValidPassword,
  useValidGivenName,
  useValidFamilyName,
  useValidPhoneNumber,
} from "../hooks/useAuthHooks";
import Email from "../components/auth/email";
import Password from "../components/auth/password";
import GivenName from "../components/auth/givenName";
import FamilyName from "../components/auth/familyName";
import PhoneNumber from "../components/auth/phoneNumber";
import { AuthContext } from "../context/authContext";
import { useLoading } from "../libs/loadingContext";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import closeIcon from "../assets/images/close.svg";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    padding: "30px",
  },
};

const SignUp = () => {
  const { email, setEmail, emailIsValid } = useValidEmail("");
  const [flag, setFlag] = useState(false);
  const { password, setPassword, passwordIsValid } = useValidPassword("");
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(59);
  const {
    phoneNumber,
    setPhoneNumber,
    countryCode,
    setCountryCode,
    phoneNumberIsValid,
  } = useValidPhoneNumber("");
  const { givenName, setGivenName, givenNameIsValid } = useValidGivenName("");
  const { familyName, setFamilyName, familyNameIsValid } =
    useValidFamilyName("");
  const [error, setError] = useState("");
  const [country, setCountry] = useState();
  const [created, setCreated] = useState(false);
  const [useOfPermission, setUseOfPermission] = useState(false);
  const [userExistsOpen, setUserExistsOpen] = useState(false);
  const { setLoading } = useLoading();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(""); // token for cylinda
  const { t } = useTranslation();
  const [alertKey, setAlertKey] = useState(null);
  const isValid =
    !emailIsValid ||
    email.length === 0 ||
    !phoneNumberIsValid ||
    phoneNumber.length === 0 ||
    !givenNameIsValid ||
    givenName.length === 0 ||
    !familyNameIsValid ||
    familyName.length === 0 ||
    !passwordIsValid ||
    password.length === 0 ||
    useOfPermission === false;

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [invalidInviteOpen, setInvalidInviteOpen] = useState(false);


  useEffect(() => {
    const id = searchParams.get("id");
    const country = searchParams.get("country");
    const storageCountry = localStorage.getItem("countryCode");

    setToken(searchParams.get("token") || "");

    if (
      searchParams.get("token") == null ||
      searchParams.get("token") == undefined ||
      searchParams.get("token") === ""
    ) {
      window.location.href = "/";
    }

    setGivenName(searchParams.get("firstname") || "");
    setFamilyName(searchParams.get("lastname") || "");
    setEmail(searchParams.get("mail") || "");
    setPhoneNumber(searchParams.get("phone") || "");

    if (storageCountry == null || storageCountry == "null") {
      localStorage.setItem("countryCode", country);
      setCountry(country?.toUpperCase());
    } else {
      setCountry(storageCountry);
    }
    // eslint-disable-next-line
  }, []);

  const signUpClicked = async () => {
    try {
      setLoading(true);
      setMinutes(1);
      setAlertKey(null);
      setSeconds(59);
      let content_type = "9999";
      let version = "9999";
      const demoUsername = uuid();
      await authContext.signUpWithEmail(
        demoUsername,
        email,
        givenName,
        familyName,
        countryCode.dial_code + phoneNumber,
        password,
        content_type,
        country,
        version,
        token,
      );
      setCreated(true);
      setFlag(true);
    } catch (err) {
      const msg = (err && err.message) || "";
      const code = err?.code || err?.name || "";

      const isInvalidInviteLink =
        /ORG_001/i.test(msg) ||
        /ORG_001/i.test(code) ||
        /invalid_invite/i.test(code) ||
        /invalid_invite/i.test(msg) ||
        /company.*missing/i.test(msg) ||
        /company.*not found/i.test(msg);
      
      const isUserExists =
        /username already exists/i.test(msg) ||
        (/already exists/i.test(msg) && /username/i.test(msg)) ||
        /UsernameExistsException/i.test(msg) ||
        /UsernameExistsException/i.test(code);
        
      if (isInvalidInviteLink) {
        setCreated(false);
        setInvalidInviteOpen(true);
        return;
      }
      if (isUserExists) {
        setCreated(false);
        setUserExistsOpen(true);
        return;
      }
      if (
        err.message ===
        "PreSignUp failed with error username or password is wrong."
      ) {
        setCreated(true);
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setFlag(false);
    }, 3000);
  }, [flag]);

  useEffect(() => {
    if (created) {
      window.location.replace(process.env.REACT_APP_AFTER_SIGNUP_URL);
    }
  }, [created]);

  useEffect(() => {
    if (created) {
      const interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }

        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setSeconds(59);
            setMinutes(minutes - 1);
          }
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [seconds, minutes, created]);

  return (
    <>
      <div className="content-page">
        <h3 className="process-title forget-password">
          {t("signUp.createAccount")}
        </h3>
        <input
          type="text"
          name="username"
          className="d-none"
          autoComplete="on"
        />
        <input
          type="text"
          name="phone-number"
          className="d-none"
          autoComplete="on"
        />
        <input
          type="password"
          name="password"
          className="d-none"
          autoComplete="on"
        />
        <div>
          <GivenName
            givenNameIsValid={givenNameIsValid}
            givenName={givenName}
            setGivenName={setGivenName}
            label={t("signUp.name")}
            required={true}
            autoComplete="off"
            name="given-name"
          />
        </div>
        <div>
          <FamilyName
            familyNameIsValid={familyNameIsValid}
            setFamilyName={setFamilyName}
            familyName={familyName}
            label={t("signUp.surname")}
            required={true}
            autoComplete="off"
            name="family-name"
          />
        </div>
        <div>
          <Email
            emailIsValid={emailIsValid}
            setEmail={setEmail}
            email={email}
            label={t("signUp.email")}
            required={true}
            autoComplete="username"
            name="username"
          />
          {error && <div className="invalid-email"></div>}
        </div>
        <div className="phoneNumberDropdown">
          <PhoneNumber
            phoneNumberIsValid={phoneNumberIsValid}
            setPhoneNumber={setPhoneNumber}
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            label={t("signUp.phone")}
            required={true}
            autoComplete="off"
            name="phone-number"
          />
        </div>
        <div>
          <Password
            passwordIsValid={passwordIsValid}
            setPassword={setPassword}
            password={password}
            label={t("signUp.password")}
            required={true}
            newPassword={true}
            autoComplete="one-time-code"
            name="password"
          />
        </div>
        <div className="form-consent">
          <div
            className="form-check"
            onClick={() => setUseOfPermission(!useOfPermission)}
          >
            {
              <>
                <svg
                  className="main-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10.625"
                    fill="white"
                    strokeWidth={2}
                    stroke={useOfPermission ? "red" : "grey"}
                  />
                </svg>
                <svg
                  className="child-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    fill={useOfPermission ? "red" : "white"}
                  />
                </svg>
              </>
            }
          </div>
          <div className="consent-desc">
            {t("signUp.readApprove")}
            <Link
              className="consent-info"
              to={`${process.env.REACT_APP_STORE_URL}content/integritetspolicy`}
              target="_blank"
            >
              {" "}
              {t("signUp.clicked")}
            </Link>
          </div>
        </div>
        <div className="signup-button">
          {isValid ? (
            <button className="btn btn-default btn-default-disabled">
              {t("signUp.createAccount")}
            </button>
          ) : (
            <button className="btn btn-default" onClick={signUpClicked}>
              {t("signUp.createAccount")}
            </button>
          )}
        </div>
        <div className="account-signin">
          {t("signUp.haveAccount")}?{" "}
          <span onClick={() => navigate("/")}>{t("signUp.login")}</span>
        </div>

        <Modal
          isOpen={userExistsOpen}
          onRequestClose={() => setUserExistsOpen(false)}
          style={customStyles}
          className="Modal modal-consent"
          overlayClassName="Overlay"
          contentLabel="user-exists-modal"
        >
          <div className="signup-modal-header">
            <h4 className="signup-modal-title">{t("signUp.accountExists")}</h4>

            <button
              onClick={() => setUserExistsOpen(false)}
              aria-label={t("common.close")}
              className="signup-modal-close-btn"
            >
              <img
                className="signup-modal-close-icon"
                src={closeIcon}
                alt={t("common.close")}
              />
            </button>
          </div>

          <p className="signup-modal-desc">{t("signUp.userExists")}</p>

          <a
            href={process.env.REACT_APP_AFTER_SIGNUP_URL || "/"}
            onClick={(e) => {
              e.preventDefault();
              setUserExistsOpen(false);
              window.location.assign(process.env.REACT_APP_AFTER_SIGNUP_URL || "/");
            }}
          >
            {t("signUp.login")}
          </a>
        </Modal>

        <Modal
          isOpen={invalidInviteOpen}
          onRequestClose={() => setInvalidInviteOpen(false)}
          style={customStyles}
          className="Modal modal-consent"
          overlayClassName="Overlay"
          contentLabel="invalid-invite-modal"
        >
          <div className="signup-modal-header">
            <h4 className="signup-modal-title">{t("signUp.invalidInviteTitle")}</h4>

            <button
              onClick={() => setInvalidInviteOpen(false)}
              aria-label={t("common.close")}
              className="signup-modal-close-btn"
            >
              <img
                className="signup-modal-close-icon"
                src={closeIcon}
                alt={t("common.close")}
              />
            </button>
          </div>

          <p className="signup-modal-desc">{t("signUp.invalidInviteDesc")}</p>
        </Modal>


      </div>
    </>
  );
};

export default SignUp;
